import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Button, Table } from 'antd';
import { InfiniteScroll, Paragraph, Spin } from 'common/components';
import { Bin, Copy } from 'common/svg';
import { LIST_LIMIT_0 } from 'common/config';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  FeedCreateModal,
  MetaDataChangeModal,
  MetaDataDeleteModal,
  WebhookUrlChangeModal,
  WebhookUrlDeleteModal,
} from 'entities/Account/components';
import { IContract } from 'entities/Contract/Contract.models';
import { SettingsCreateCustomerIdCard } from 'entities/Settings/components/SettingsCreateCustomerIdCard';
import { renderSettingsIntegrationsBuyerListRecords } from 'entities/Settings/Settings.helper';
import {
  generateAccountWebhookSign,
  getAccountById,
  selectAccount,
  selectIsAccountLoading,
  updateAccount,
} from 'entities/Account/Account.slice';
import { getLegalList, selectIsLegalLoading } from 'entities/LegalEntity/Legal.slice';
import { IUpdateAccountPayload } from 'entities/Account/Account.models';
import { selectContractList } from 'entities/Contract/Contract.slice';
import { selectCurrentUser } from 'entities/User/User.slice';

interface IComponentProps {
  header: ReactNode;
}

export const SettingsAccountIntegrationsTab: FC<IComponentProps> = ({ header }) => {
  const dispatch = useAppDispatch();
  // account
  const account = useAppSelector(selectAccount);
  const isAccountLoading = useAppSelector(selectIsAccountLoading);
  // contract
  const contractList = useAppSelector(selectContractList);
  // legal
  const isLegalLoading = useAppSelector(selectIsLegalLoading);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [openFeedCreateModal, setOpenFeedCreateModal] = useState<boolean>(false);
  const [openWebhookUrlChangeModal, setOpenWebhookUrlChangeModal] = useState<boolean>(false);
  const [openWebhookUrlDeleteModal, setOpenWebhookUrlDeleteModal] = useState<boolean>(false);
  const [openMetaDataChangeModal, setOpenMetaDataChangeModal] = useState<boolean>(false);
  const [openMetaDataDeleteModal, setOpenMetaDataDeleteModal] = useState<boolean>(false);
  const [contract, setContract] = useState<IContract | null>(null);
  const [openSettingsCreateCustomerIdCard, setOpenSettingsCreateCustomerIdCard] = useState<boolean>(false);

  const accountId = currentUser?.account?.id;

  const onAccountFeedUrlUpdate = ({ onSuccess, feedUrl }: Omit<IUpdateAccountPayload, 'id'>) => {
    if (!account) {
      return;
    }

    dispatch(
      updateAccount({
        id: account.id,
        feedUrl,
        onSuccess,
      }),
    );
  };

  const onAccountWebhookUrlUpdate = ({ onSuccess, webhookUrl }: Omit<IUpdateAccountPayload, 'id'>) => {
    if (!account) {
      return;
    }

    dispatch(
      updateAccount({
        id: account.id,
        webhookUrl,
        onSuccess,
      }),
    );
  };

  const onAccountWebhookUrlDelete = () => {
    if (!account) {
      return;
    }

    dispatch(
      updateAccount({
        id: account.id,
        webhookUrl: null,
        onSuccess: () => setOpenWebhookUrlDeleteModal(false),
      }),
    );
  };

  const onAccountMetadataUpdate = ({ onSuccess, metadata }: Omit<IUpdateAccountPayload, 'id'>) => {
    if (!account) {
      return;
    }

    dispatch(
      updateAccount({
        id: account.id,
        metadata,
        onSuccess,
      }),
    );
  };

  const onAccountMetaDataDelete = () => {
    if (!account) {
      return;
    }

    dispatch(
      updateAccount({
        id: account.id,
        // @ts-ignore
        metadata: null,
        onSuccess: () => setOpenMetaDataDeleteModal(false),
      }),
    );
  };

  const handleGenerateAccountWebhookSign = (): void => {
    dispatch(generateAccountWebhookSign());
  };

  useEffect(() => {
    const fetch = async () => {
      if (accountId) {
        await dispatch(getAccountById(accountId));
      }

      setFetchLoading(false);
    };

    fetch();
  }, []);

  return (
    <>
      <InfiniteScroll>
        {header}

        <Spin spinning={fetchLoading}>
          <div className="mb-14 flex wrap account-integrations-settings__container">
            <div className="account-integrations-settings__block">
              <div className="mb-3 ff-medium fs-m">Настройка интеграции</div>

              <div className="mb-6 ff-regular fs-xs color-dark-grey">
                Настройте интеграцию каталога, указав ссылку на фид для автоматического экспорта товаров на платформу. Убедитесь,
                что данные актуальны и доступны в нужном формате.
              </div>

              {account?.feedUrl && (
                <div className="mb-6">
                  <Paragraph
                    ellipsis={{ rows: 1, expandable: true }}
                    classNames={{ text: 'ff-medium fs-s', toggle: 'ff-semibold fs-xs' }}
                    color={{ toggle: '#387D61' }}
                    copyable
                    copyIcon={<Copy className="icon-dark-grey cursor-pointer" />}
                  >
                    {account.feedUrl}
                  </Paragraph>
                </div>
              )}

              <Button
                className={`button-sm ${account?.feedUrl?.length ? 'secondary' : 'primary'}`}
                onClick={() => setOpenFeedCreateModal(true)}
              >
                {account?.feedUrl?.length ? 'Изменить фид' : 'Добавить фид'}
              </Button>
            </div>

            <div className="account-integrations-settings__block">
              <div className="mb-3 ff-medium fs-m">Настройка webhookUrl</div>

              <div className="mb-6 ff-regular fs-xs color-dark-grey">
                Добавьте адрес (URL), на который система будет отправлять данные заказа при его передаче поставщику. Используется
                для интеграции и создания заказа в 1С.
              </div>

              {account?.webhookUrl && (
                <div className="mb-6">
                  <Paragraph
                    ellipsis={{ rows: 1, expandable: true }}
                    classNames={{ text: 'ff-medium fs-s', toggle: 'ff-semibold fs-xs' }}
                    color={{ toggle: '#387D61' }}
                    copyable
                    copyIcon={<Copy className="icon-dark-grey cursor-pointer" />}
                  >
                    {account.webhookUrl}
                  </Paragraph>
                </div>
              )}

              <div className="flex align-center gap-4">
                <Button
                  className={`button-sm ${account?.webhookUrl?.length ? 'secondary' : 'primary'}`}
                  onClick={() => setOpenWebhookUrlChangeModal(true)}
                >
                  {account?.webhookUrl?.length ? 'Изменить URL' : 'Добавить URL'}
                </Button>

                {account?.webhookUrl && (
                  <Button
                    className="button-circle deleting"
                    icon={<Bin className="icon-bin-pale-red" />}
                    onClick={() => setOpenWebhookUrlDeleteModal(true)}
                  />
                )}
              </div>
            </div>

            <div className="account-integrations-settings__block">
              <div className="mb-3 ff-medium fs-m">Токен webhookSign</div>

              <div className="mb-6 ff-regular fs-xs color-dark-grey">
                Автоматическая генерация токена для подписи и проверки подлинности содержимого вебхука. Применяется на стороне 1С
                для расшифровки и валидации полученных данных.
              </div>

              {account?.webhookSign && (
                <div className="mb-6">
                  <Paragraph
                    ellipsis={{ rows: 1, expandable: true }}
                    classNames={{ text: 'ff-medium fs-s', toggle: 'ff-semibold fs-xs' }}
                    color={{ toggle: '#387D61' }}
                    copyable
                    copyIcon={<Copy className="icon-dark-grey cursor-pointer" />}
                  >
                    {account.webhookSign}
                  </Paragraph>
                </div>
              )}

              <Button className="button-sm primary" onClick={handleGenerateAccountWebhookSign} loading={isAccountLoading}>
                Создать новый
              </Button>
            </div>

            <div className="account-integrations-settings__block">
              <div className="mb-3 ff-medium fs-m">Настройка meta_data</div>

              <div className="mb-6 ff-regular fs-xs color-dark-grey">
                В meta_data вы можете указать данные в формате JSON, которые будут добавляться в ответ к каждому заказу (например,
                основной склад - warehouse_id).
              </div>

              {account?.metadata && (
                <div className="mb-6">
                  <Paragraph
                    ellipsis={{ rows: 1, expandable: true }}
                    classNames={{ text: 'ff-medium fs-s', toggle: 'ff-semibold fs-xs' }}
                    color={{ toggle: '#387D61' }}
                    copyable
                    copyIcon={<Copy className="icon-dark-grey cursor-pointer" />}
                  >
                    {JSON.stringify(account.metadata)}
                  </Paragraph>
                </div>
              )}

              <div className="flex align-center gap-4">
                <Button
                  className={`button-sm ${account?.metadata ? 'secondary' : 'primary'}`}
                  onClick={() => setOpenMetaDataChangeModal(true)}
                  loading={isAccountLoading}
                >
                  {account?.metadata ? 'Изменить meta_data' : 'Добавить meta_data'}
                </Button>

                {account?.metadata && (
                  <Button
                    className="button-circle deleting"
                    icon={<Bin className="icon-bin-pale-red" />}
                    onClick={() => setOpenMetaDataDeleteModal(true)}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="mb-3 ff-medium fs-m">ID покупателей и грузополучателей (адресов доставки)</div>

          <div className="mb-6 ff-regular fs-xs color-dark-grey" style={{ maxWidth: 700 }}>
            Настройте ID для идентификации юридических лиц и адресов доставки покупателя в системе учёта.
          </div>

          <div>
            <Spin spinning={isLegalLoading}>
              <Table
                showHeader={false}
                pagination={false}
                dataSource={contractList.map((contract) => ({ ...contract, key: contract.id }))}
                columns={renderSettingsIntegrationsBuyerListRecords()}
                onRow={(record) => {
                  return {
                    style: { cursor: 'pointer' },
                    onClick: async () => {
                      await dispatch(
                        getLegalList({
                          accountId: record.buyerId,
                          limit: LIST_LIMIT_0,
                        }),
                      );
                      setContract(record);
                      setOpenSettingsCreateCustomerIdCard(true);
                    },
                  };
                }}
              />
            </Spin>
          </div>
        </Spin>
      </InfiniteScroll>

      <FeedCreateModal
        open={openFeedCreateModal}
        loading={isAccountLoading}
        url={account?.feedUrl}
        onClose={() => setOpenFeedCreateModal(false)}
        onSave={onAccountFeedUrlUpdate}
      />

      <WebhookUrlChangeModal
        open={openWebhookUrlChangeModal}
        loading={isAccountLoading}
        url={account?.webhookUrl}
        onClose={() => setOpenWebhookUrlChangeModal(false)}
        onSave={onAccountWebhookUrlUpdate}
      />

      <WebhookUrlDeleteModal
        open={openWebhookUrlDeleteModal}
        loading={isAccountLoading}
        onClose={() => setOpenWebhookUrlDeleteModal(false)}
        onAccept={onAccountWebhookUrlDelete}
      />

      <MetaDataChangeModal
        open={openMetaDataChangeModal}
        loading={isAccountLoading}
        metadata={account?.metadata}
        onClose={() => setOpenMetaDataChangeModal(false)}
        onSave={onAccountMetadataUpdate}
      />

      <MetaDataDeleteModal
        open={openMetaDataDeleteModal}
        loading={isAccountLoading}
        onClose={() => setOpenMetaDataDeleteModal(false)}
        onAccept={onAccountMetaDataDelete}
      />

      <SettingsCreateCustomerIdCard
        open={openSettingsCreateCustomerIdCard}
        contract={contract}
        onClose={() => setOpenSettingsCreateCustomerIdCard(false)}
      />
    </>
  );
};
